import { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SiteHeader from "@landing/components/SiteHeader";
import PageSEO from "@landing/components/PageSEO";
import { Sidebar } from "./Sidebar";
import { navigation } from "./navigation";
import { searchIndex } from "../data/searchIndex";

interface DocsLayoutProps {
  children: ReactNode;
  product?: string;
}

const PRODUCT_LABELS: Record<string, string> = {
  flows: "Flows",
  admin: "Admin",
  chat: "Chat",
  "mcp-server": "MCP Server",
  runtime: "Agent Runtime",
};

function useDocMeta(pathname: string) {
  if (pathname === "/docs") {
    return {
      title: "Anter Docs — Managed AI Agent Architecture & Platform Reference",
      description:
        "Everything you need to set up, build, orchestrate, and operate AI agents on Anter.",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Anter Docs",
        url: "https://anter.ai/docs",
      } as Record<string, unknown>,
    };
  }

  const entry = searchIndex.find((e) => e.href === pathname);
  const nav = navigation.find((n) => n.basePath === pathname);

  const productLabel = entry?.product
    ? (PRODUCT_LABELS[entry.product] ?? entry.product)
    : nav
      ? PRODUCT_LABELS[nav.product]
      : undefined;

  const title = nav
    ? `${nav.label} — Anter Docs`
    : entry
      ? `${entry.title}${productLabel ? ` — ${productLabel}` : ""} Docs | Anter`
      : "Anter Docs";

  const description = entry?.description ?? "Build, configure, and operate AI agents with Anter.";
  const fullUrl = `https://anter.ai${pathname}`;

  const jsonLd = entry
    ? [
        {
          "@context": "https://schema.org",
          "@type": "TechArticle",
          headline: `${entry.title}${productLabel ? ` — ${productLabel}` : ""}`,
          name: entry.title,
          description: entry.description,
          url: fullUrl,
          inLanguage: "en-US",
          mainEntityOfPage: fullUrl,
          isPartOf: {
            "@type": "WebSite",
            name: "Anter Docs",
            url: "https://anter.ai/docs",
          },
          publisher: {
            "@type": "Organization",
            name: "Anter",
            url: "https://anter.ai",
            logo: "https://anter.ai/favicon.svg",
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Docs",
              item: "https://anter.ai/docs",
            },
            ...(productLabel
              ? [
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: productLabel,
                    item: nav?.basePath
                      ? `https://anter.ai${nav.basePath}`
                      : `https://anter.ai/docs/${entry.product}`,
                  },
                  {
                    "@type": "ListItem",
                    position: 3,
                    name: entry.title,
                    item: fullUrl,
                  },
                ]
              : [
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: entry.title,
                    item: fullUrl,
                  },
                ]),
          ],
        },
      ]
    : nav
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Docs",
                item: "https://anter.ai/docs",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: nav.label,
                item: fullUrl,
              },
            ],
          },
        ]
      : undefined;

  return { title, description, jsonLd, entry, nav, productLabel };
}

export function DocsLayout({ children, product }: DocsLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { title, description, jsonLd, entry, nav, productLabel } = useDocMeta(location.pathname);

  const detectedProduct =
    product ?? navigation.find((n) => location.pathname.startsWith(n.basePath))?.product;

  const currentProductNav = navigation.find((n) => n.product === (product ?? detectedProduct));

  return (
    <>
      <PageSEO
        title={title}
        description={description}
        canonical={location.pathname}
        ogType="article"
        jsonLd={jsonLd}
      />
      {/* SiteHeader is fixed at z-40, height = 48px (h-12) */}
      <SiteHeader
        scrolled
        onNavigate={(href) => {
          if (href.startsWith("/docs")) {
            navigate(href);
          } else {
            // Must hard-reload when crossing from docs SPA to landing SPA document shell
            window.location.href = href;
          }
        }}
      />

      {/* pt-12 pushes the page body below the fixed SiteHeader */}
      <div className="min-h-dvh flex flex-col pt-12 bg-bg">
        {/* Docs product subnav — sticks just below the SiteHeader */}
        <header
          aria-label="Documentation products"
          className="border-b border-border bg-bg-secondary sticky top-12 z-10"
        >
          <div className="max-w-7xl mx-auto px-6 h-12 flex items-center">
            <nav
              aria-label="Documentation products"
              className="flex items-center gap-1 overflow-x-auto"
            >
              {navigation.map((n) => {
                const Icon = n.icon;
                const isActive = location.pathname.startsWith(n.basePath);
                return (
                  <Link
                    key={n.product}
                    to={n.basePath}
                    aria-current={isActive ? "page" : undefined}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-bg-elevated text-text font-medium"
                        : "text-text-muted hover:text-text hover:bg-bg-hover"
                    }`}
                  >
                    <Icon size={13} aria-hidden="true" />
                    {n.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </header>

        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 flex flex-1">
          {detectedProduct && <Sidebar product={detectedProduct} />}
          <main
            id="main-content"
            tabIndex={-1}
            className="flex-1 min-w-0 py-8 px-0 md:px-8 docs-prose"
            data-doc-content
          >
            {currentProductNav && (
              <details className="md:hidden mb-6 rounded-lg border border-border bg-bg-secondary">
                <summary className="cursor-pointer px-3 py-2.5 text-sm font-medium text-text">
                  On this product
                </summary>
                <nav aria-label="On this product" className="px-3 pb-3 flex flex-col gap-1">
                  {currentProductNav.sections.flatMap((section) =>
                    section.items.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        aria-current={location.pathname === item.href ? "page" : undefined}
                        className={`rounded-md px-2 py-1.5 text-sm ${
                          location.pathname === item.href
                            ? "bg-primary-dim text-primary-text font-medium"
                            : "text-text-muted hover:text-text hover:bg-bg-hover"
                        }`}
                      >
                        {item.label}
                      </Link>
                    )),
                  )}
                </nav>
              </details>
            )}
            {/* Visual Breadcrumbs */}
            {location.pathname !== "/docs" && (
              <nav
                aria-label="Breadcrumb"
                className="mb-6 flex items-center gap-1.5 text-xs text-text-faint"
              >
                <Link to="/docs" className="transition-colors hover:text-text-muted">
                  Docs
                </Link>
                {(productLabel || currentProductNav) && (
                  <>
                    <ChevronRight size={12} className="text-text-faint/60" aria-hidden="true" />
                    <Link
                      to={
                        currentProductNav?.basePath ?? nav?.basePath ?? `/docs/${detectedProduct}`
                      }
                      className="transition-colors hover:text-text-muted"
                    >
                      {productLabel ?? currentProductNav?.label}
                    </Link>
                  </>
                )}
                {entry?.title && (
                  <>
                    <ChevronRight size={12} className="text-text-faint/60" aria-hidden="true" />
                    <span className="font-medium text-text" aria-current="page">
                      {entry.title}
                    </span>
                  </>
                )}
              </nav>
            )}
            {children}
          </main>
        </div>
      </div>
    </>
  );
}
